<template>
  <div style="min-height: calc(100vh - 158px);" class="card">
    <div style="background: #DBE7F2" class="p-2">
      <TitleButton
          btnTitle="Add New"
          :showBtn="false"
          :showAddNew="!metaData.is_shareholder"
          @onClickAddNewButton="openModal"
      />
    </div>

    <div
        v-for="master in tableItems"
        :key="master.id"
        class="card px-2 mb-1" style="min-height: 200px"
    >
      <div class="card-header d-flex justify-content-between align-items-center">
        <div>
          <strong>{{ master.project_name }}</strong>
          <small>
            ({{ master.start_date }} → {{ master.end_date }})
          </small>
        </div>

        <button v-if="canEditPlan(master)"
            @click="openEditModal(master)"
            class="btn btn-sm btn-outline-primary"
        >
          Edit
        </button>
      </div>

      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table mb-0 table-bordered">
            <thead class="table-light">
            <tr>
              <th>Payment Type</th>
              <th>Schedule Date</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
            </thead>
            <tbody>
            <tr
                v-for="general in master.generals"
                :key="general.id"
            >
              <td>{{ general.title }}</td>
              <td>
                <input v-if="editingId === general.id" type="date" class="form-control form-control-sm" v-model="editForm.date">
                <span v-else>{{ general.date }}</span>
              </td>
              <td>
                <input v-if="editingId === general.id" type="number" class="form-control form-control-sm" v-model="editForm.amount">
                <span v-else>{{ general.amount }}</span>
              </td>
              <td>
            <span
                :class="[
                'badge text-capitalize',
                general.status === 'due'
                  ? 'bg-warning'
                  : 'bg-success'
              ]"
            >
              {{ general.status }}
            </span>
              </td>
              <td v-if="editingId === general.id">
                <button :disabled="saving" @click="saveEdit(general)" class="btn btn-sm btn-primary">
                  <span v-if="saving" class="spinner-border spinner-border-sm" role="status"></span>
                  Save
                </button>
                <button :disabled="saving" @click="cancelEdit" class="btn btn-sm btn-outline-secondary" style="margin-left: 10px">
                  Cancel
                </button>
              </td>
              <td v-else>
                <button v-if="general.status === 'due' && !metaData.is_shareholder" @click="openMoneyReceiptModal(general)" class="btn btn-sm btn-outline-primary">
                  Pay Now
                </button>
                <button v-if="general.status === 'due' && !metaData.is_shareholder && isSoftwareAdmin"
                        @click="startEdit(general)"
                        class="btn btn-sm btn-outline-secondary"
                        style="margin-left: 10px"
                >
                  <i class="fa fa-pencil-alt"></i>
                </button>
                <button v-if="general.status === 'paid' && ! metaData.is_shareholder && isSoftwareAdmin"
                        @click="openMoneyReceiptModal(general, true)"
                        class="btn btn-sm btn-primary"
                        style="margin-left: 10px"
                >
                  <i class="fa fa-eye"></i>
                </button>
                <button class="btn btn-secondary btn-sm"
                        @click="goToPrint(general.receipt_master_id)"
                        v-if="general.status === 'paid'"
                        style="cursor: pointer; margin-left: 10px"
                >
                  <i class="fas fa-print"></i>
                </button>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>    <div class="mb-2"></div>
    <Loader v-if="loading"/>

    <InstallmentPlanSetupModal
        v-if="isModalOpen"
        :contact="selectedContact"
        :master="selectedMaster"
        @close="closeModal"
        @getData="getBillPaymentData"
    />

    <MoneyReceiptModal
        v-if="isMoneyReceiptModalOpen"
        :contact="selectedContact"
        :generalData="generalData"
        :isEdit="isMoneyReceiptEdit"
        @close="closeMoneyReceiptModal"
        @getData="getBillPaymentData"
    />
  </div>
</template>

<script setup>
import TitleButton from "@/components/atom/TitleButton.vue";
import {ref, watch, inject, onMounted} from "vue";
import Loader from "@/components/atom/LoaderComponent.vue";
import InstallmentPlanSetupModal from "@/views/page/company/procurement/shareholder-register/bill-payment/InstallmentPlanSetupModal.vue";
import MoneyReceiptModal from "@/views/page/company/procurement/shareholder-register/bill-payment/MoneyReceiptModal.vue";
import handleProjects from "@/services/modules/procurement/project";
import handlePurchase from "@/services/modules/purchase";
import handleCompany from "@/services/modules/company";
import moneyReceiptHelper from "@/services/utils/pdf/moneyReceiptHelper";
import {useRoute} from "vue-router";

const loading = ref(false)
const isModalOpen = ref(false)
const isMoneyReceiptModalOpen = ref(false)
const isMoneyReceiptEdit = ref(false)
const isSoftwareAdmin = ref(false)
const selectedContact = ref(null)
const selectedMaster = ref(null)
const generalData = ref({})
const tableItems = ref([])
const editingId = ref(null)
const editForm = ref({ date: null, amount: null })
const saving = ref(false)
const $route = useRoute();

const props = defineProps({
  contactInfo: {
    type: Object,
    default: {
      full_name: '',
      profile_items: [],
      contact: {},
    }
  },
  contactDetails: {
    type: Array,
    default: []
  },
  metaData: {
    type: Array,
    default: []
  }
})

const showError = inject('showError')
const showSuccess = inject('showSuccess')
const { getProjectInstallmentSetupData, updateInstallment } = handleProjects();
const { fetchMoneyReceipt } = handlePurchase();
const { fetchCompanyInfo } = handleCompany();
const { generatePdf } = moneyReceiptHelper();

const companyInfo = ref({})
const moneyReceipt = ref({})

const getCompanyInfo = async () => {
  const res = await fetchCompanyInfo($route.params.companyId);
  if (!res.status) return;
  companyInfo.value = res.data;
}

const openModal = () => {
    selectedMaster.value = null
    isModalOpen.value = true;
    selectedContact.value = props.contactInfo
}

const openEditModal = (master) => {
  selectedMaster.value = master
  isModalOpen.value = true
  selectedContact.value = props.contactInfo
}

const canEditPlan = (master) => {
  if (props.metaData.is_shareholder) return false
  return !master.generals.some(general => general.status === 'paid' || general.receipt_master_id)
}

const openMoneyReceiptModal = (item, isEdit = false) => {
  isMoneyReceiptModalOpen.value = true;
  isMoneyReceiptEdit.value = isEdit
  selectedContact.value = props.contactInfo
  generalData.value = item
}

const closeModal = () => {
  isModalOpen.value = false
}

const closeMoneyReceiptModal = () => {
  isMoneyReceiptModalOpen.value = false
  isMoneyReceiptEdit.value = false
}

const startEdit = (general) => {
  editingId.value = general.id
  editForm.value = { date: general.date, amount: general.amount }
}

const cancelEdit = () => {
  editingId.value = null
}

const saveEdit = async (general) => {
  if (!editForm.value.date || editForm.value.amount === '' || editForm.value.amount === null) {
    showError('Schedule date and amount are required')
    return
  }
  saving.value = true
  const query = '?company_id=' + $route.params.companyId
  const payload = {
    company_id: $route.params.companyId,
    date: editForm.value.date,
    amount: editForm.value.amount,
  }
  try {
    const res = await updateInstallment(general.id, query, payload)
    if (!res.status) {
      showError(res.message)
      return
    }
    showSuccess(res.message)
    editingId.value = null
    getBillPaymentData()
  } catch (err) {
    showError('Something is wrong. Check your connectivity!!')
  } finally {
    saving.value = false
  }
}

const goToPrint = (id) => {
  const companyQuery = `?company_id=${$route.params.companyId}`;
  fetchMoneyReceipt(id, companyQuery).then((res) => {
    if (!res.status) return;
    moneyReceipt.value = res.data;
  }).then(() => {
    generatePdf(companyInfo.value, moneyReceipt.value)
  }).catch(() => {
    showError('Something is wrong. Check your connectivity!!')
  })
}

watch(
    () => props.contactInfo,
    (newVal) => {
      if (newVal && newVal.id) {
        getBillPaymentData();
      }
    },
    { immediate: true }
);

const getQuery = () => {
  let query = '?company_id=' + $route.params.companyId
   query += '&contact_profile_id=' + props.contactInfo.id
  return query
};

async function getBillPaymentData() {
  let query = getQuery();
  try {
    const res = await getProjectInstallmentSetupData(query);
    if (! res.status) {
      tableItems.value = [];
      return
    }
    isSoftwareAdmin.value = res.is_software_admin === true
    tableItems.value = res.data;
  } catch (err) {

  }
}

onMounted(() => {
  getCompanyInfo();
})
</script>
